import { FormikValues } from 'formik';
import {
    focCheckboxValues,
    focVarsFromField,
    groupingValidationMessage,
    qualityCheckboxValues,
    qualityVarsFromField,
} from '~/util';

interface AnswerType {
    fieldType: string;
    value: string;
    detail: MeasureDetailType;
    action?: FocActionType | MeasureDetailType;
    checkboxFieldId: string;
}
interface AnswerMappingType {
    [key: string]: AnswerType;
}

function focAnswerMapping(values: FormikValues, measure: GapType['measure']) {
    const mapping: AnswerMappingType = {};
    Object.entries(values).forEach(([fieldId, value]) => {
        const { fieldType, detailId, actionId } = focVarsFromField(fieldId)!;
        if (fieldType === 'date' || fieldType === 'answer') {
            const detail = measure?.focs
                ?.map((foc) => foc.foc_actions?.map((fga) => fga.foc_action_details).flat())
                .flat()
                .find((fad) => fad?.id === detailId);
            const action = measure?.focs
                ?.map((foc) => foc.foc_actions)
                .flat()
                .find((fga) => fga?.id === actionId);
            if (detail) {
                mapping[fieldId] = {
                    fieldType,
                    value,
                    action,
                    detail,
                    checkboxFieldId: `foc_action_${action?.id}_checked`,
                };
            }
        }
    });
    return mapping;
}

function qualityAnswerMapping(values: FormikValues, measure: GapType['measure']) {
    const mapping: AnswerMappingType = {};
    Object.entries(values).forEach(([fieldId, value]) => {
        const { detailId, fieldType } = qualityVarsFromField(fieldId);
        if (fieldType === 'date' || fieldType === 'answer') {
            const detail = measure?.quality_measure_details?.find((qmd) => qmd.id === detailId);
            if (detail) {
                mapping[fieldId] = {
                    fieldType,
                    value,
                    detail,
                    action: detail,
                    checkboxFieldId: `quality_measure_detail_${detail.id}_${detail.system_id}_checked`,
                };
            }
        }
    });

    return mapping;
}

function compareGroupingEntries(
    mapping: AnswerMappingType,
    checkboxValues: { [key: string]: boolean }
) {
    const errors: { [key: string]: string } = {};

    // Loop over mapping and for Answered fields search for Unanswered fields
    // with same response group
    Object.entries(mapping).forEach(([, fieldVars1]) => {
        // Make sure it is Checked and Answered
        if (!fieldVars1.value || !checkboxValues[fieldVars1.action!.id]) return;
        const found: { [key: number]: (AnswerType & { id: string })[] } = {};
        fieldVars1.detail.response_grouping?.forEach((rg) => {
            found[rg] = [];
        });
        Object.entries(mapping).forEach(([fieldId2, fieldVars2]) => {
            // Answered & checked
            if (fieldVars2.value && checkboxValues[fieldVars2.action!.id]) return;
            // Same data type id gets skipped, historical reasons, find out why
            // Maybe because date and answer field will have the same value here?
            if (fieldVars1.detail.data_type_id === fieldVars2.detail.data_type_id) return;

            fieldVars1.detail.response_grouping?.forEach((rg) => {
                if (fieldVars2.detail.response_grouping?.includes(rg)) {
                    found[rg].push({ id: fieldId2, ...fieldVars2 });
                }
            });
        });

        // If at least one of the response groupings passed, we're good to go
        if (Object.values(found).some((x) => x.length === 0)) return;

        Object.values(found).forEach((rg) => {
            rg.forEach((fieldVars2) => {
                if (!checkboxValues[fieldVars2.action!.id]) {
                    // Set error on checkbox since the fields aren't open
                    errors[fieldVars2.checkboxFieldId] = groupingValidationMessage(
                        fieldVars1.detail
                    );
                } else if (fieldVars2.fieldType === 'answer') {
                    // Set error on answer field
                    errors[fieldVars2.id] = groupingValidationMessage(fieldVars1.detail);
                }
            });
        });
    });
    return errors;
}

export function focGroupingValidation(values: FormikValues, measure: GapType['measure']) {
    return compareGroupingEntries(focAnswerMapping(values, measure), focCheckboxValues(values));
}

export function qualityGroupingValidation(values: FormikValues, measure: GapType['measure']) {
    return compareGroupingEntries(
        qualityAnswerMapping(values, measure),
        qualityCheckboxValues(values)
    );
}
